import { useState } from "react";
import {
  Search,
  ListFilter,
  Plus,
  Edit3,
  Trash2,
  X,
  Package,
  Loader,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useProducts } from "../../hooks/useProducts";
import { useCategories } from "../../hooks/useCategories";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import { useProductStats } from "../../hooks/useProductStats.js";
import DeleteModal from "../../modals/productModal/deleteModal";
import Pagination from "../../components/Pagination";
import formatCurrency from "../../utils/formatCurrency";

const Product = () => {
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 400);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const navigate = useNavigate();

  const handleDeleteClick = (product) => {
    setSelectedProduct(product);
    setDeleteModalOpen(true);
  };
  const { stats } = useProductStats();
  const { categories } = useCategories();
  // UI filter state (for dropdowns)
  const [filterDraft, setFilterDraft] = useState({
    category: "",
    status: "",
    stock: "",
    gender: "",
    bestSeller: "",
    minPrice: "",
    maxPrice: "",
  });
  // Actual filter state (for endpoint)
  const [appliedFilters, setAppliedFilters] = useState({
    category: "",
    status: "",
    stock: "",
    gender: "",
    bestSeller: "",
    minPrice: "",
    maxPrice: "",
  });

  const { products, loading, error, pagination } = useProducts(
    currentPage,
    debouncedSearch || "",
    appliedFilters.category,
    appliedFilters.status,
    appliedFilters.stock,
    appliedFilters.gender,
    appliedFilters.bestSeller,
    appliedFilters.minPrice,
    appliedFilters.maxPrice,
  );


  const statsData = [
    { label: "Total Products", value: stats?.total_products ?? 0 },
    { label: "Active Products", value: stats?.active_products ?? 0 },
    { label: "Low Stock", value: stats?.low_stock ?? 0 },
    { label: "Out of Stock", value: stats?.out_of_stock ?? 0 },
    { label: "Inventory Value", value: formatCurrency(stats?.inventory_value) },
  ];

  return (
    <div className="min-h-screen bg-white p-4 sm:p-6 md:p-8 font-sans text-slate-700">
      {/* --- HEADER --- */}
      <div className="flex flex-col sm:flex-row justify-between items-start mb-4 sm:mb-8 gap-3">
        <div>
          <h1 className="text-lg sm:text-xl font-medium text-[#0C0C0E]">
            Products
          </h1>
          <p className="font-medium text-xs sm:text-sm text-[#7D828D]">
            Manage your product inventory
          </p>
        </div>
        <button
          onClick={() => navigate("/product/add")}
          className="flex items-center gap-2 bg-[#D2BDA1] text-black text-xs sm:text-sm px-3 sm:px-4 py-2 rounded-lg font-medium hover:bg-[#c5b49f] transition-colors active:scale-95 active:shadow-sm duration-100 w-full sm:w-auto justify-center"
        >
          <Plus size={18} /> Add Product
        </button>
      </div>

      {/* --- STATS CARDS --- */}
      <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-0 mb-6 sm:mb-10 border-y border-[#EEF1F3]">
        {statsData.map((stat, index) => (
          <div
            key={index}
            className={`p-4 sm:p-6 ${index !== statsData.length - 1 ? "border-r border-[#EEF1F3] first:border-l-0 last:border-r-0" : ""}`}
          >
            <p className="text-[#7D828D] font-medium text-xs sm:text-sm mb-1 sm:mb-2">
              {stat.label}
            </p>
            <p
              className={`text-lg sm:text-2xl font-medium break-words ${stat.color}`}
            >
              {stat.value}
            </p>
          </div>
        ))}
      </div>

      <h2 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-4 text-[#0C0C0E]">
        Product List
      </h2>

      {/* --- TOOLBAR & FILTER --- */}
      <div className="relative mb-4 sm:mb-6 flex flex-col sm:flex-row gap-3 sm:gap-4">
        <div className="relative flex-1 max-w-full sm:max-w-sm">
          <Search
            className="text-xs sm:text-sm absolute left-3 top-1/2 -translate-y-1/2 text-[#7D828D]"
            size={18}
          />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            placeholder="Search product by name, SKU..."
            className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D9CBBF] text-xs sm:text-sm"
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="bg-white border border-[#D2BDA1] px-3 sm:px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-slate-50 transition-colors text-xs sm:text-sm w-full sm:w-auto justify-center"
        >
          <ListFilter size={18} /> Filters
        </button>
        {/* Filter Dropdown Overlay */}
        {showFilters && (
          <div className="absolute top-12 left-0 z-10 max-w-full sm:max-w-[855px] w-full bg-white rounded-xl border border-[#EAEAEA] p-4 sm:p-6 animate-in fade-in zoom-in duration-150">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-2 sm:gap-0">
              <span className="font-medium text-sm sm:text-base text-[#0C0C0E]">
                Filters
              </span>
              <X
                className="cursor-pointer text-slate-400"
                size={20}
                onClick={() => setShowFilters(false)}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 sm:mb-8">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-[#314158] mb-2 sm:mb-4">
                  Category
                </label>
                <select
                  className="w-full p-2 bg-[#FBF8F6] border border-[#FEF0C7] rounded-lg text-[#0C0C0E] text-xs sm:text-sm outline-none"
                  value={filterDraft.category}
                  onChange={(e) =>
                    setFilterDraft((d) => ({ ...d, category: e.target.value }))
                  }
                >
                  <option value="">All Categories</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-[#314158] mb-2 sm:mb-4">
                  Status
                </label>
                <select
                  className="w-full p-2 bg-[#FBF8F6] border border-[#FEF0C7] rounded-lg text-[#0C0C0E] text-xs sm:text-sm outline-none"
                  value={filterDraft.status}
                  onChange={(e) =>
                    setFilterDraft((d) => ({ ...d, status: e.target.value }))
                  }
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-[#314158] mb-2 sm:mb-4">
                  Stock Level
                </label>
                <select
                  className="w-full p-2 bg-[#FBF8F6] border border-[#FEF0C7] rounded-lg text-[#0C0C0E] text-xs sm:text-sm outline-none"
                  value={filterDraft.stock}
                  onChange={(e) =>
                    setFilterDraft((d) => ({ ...d, stock: e.target.value }))
                  }
                >
                  <option value="">All Stock Levels</option>
                  <option value="in_stock">In Stock</option>
                  <option value="low_stock">Low Stock</option>
                  <option value="out_of_stock">Out of Stock</option>
                </select>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-[#314158] mb-2 sm:mb-4">
                  Collection
                </label>
                <select
                  className="w-full p-2 bg-[#FBF8F6] border border-[#FEF0C7] rounded-lg text-[#0C0C0E] text-xs sm:text-sm outline-none"
                  value={filterDraft.gender}
                  onChange={(e) =>
                    setFilterDraft((d) => ({ ...d, gender: e.target.value }))
                  }
                >
                  <option value="">All Collections</option>
                  <option value="male">Men</option>
                  <option value="female">Women</option>
                  <option value="kids">Kids</option>
                  <option value="unisex">Unisex</option>
                </select>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center pt-4 sm:pt-4 border-t border-slate-100 gap-2 sm:gap-0">
              <button
                className="text-slate-600 text-xs sm:text-sm px-4 py-2 font-medium border-[3px] border-[#F5F5F5] bg-gradient-to-r from-[white] to-[#F5F5F5] hover:underline shadow-sm rounded-lg active:scale-95 transition-transform duration-100"
                onClick={() => {
                  setFilterDraft({
                    category: "",
                    status: "",
                    stock: "",
                    gender: "",
                    bestSeller: "",
                    minPrice: "",
                    maxPrice: "",
                  });
                  setAppliedFilters({
                    category: "",
                    status: "",
                    stock: "",
                    gender: "",
                    bestSeller: "",
                    minPrice: "",
                    maxPrice: "",
                  });
                  setShowFilters(false);
                  setCurrentPage(1);
                }}
              >
                Reset Filter
              </button>
              <button
                className="bg-[#D2BDA1] px-6 sm:px-8 py-2 rounded-lg font-medium text-slate-900 text-xs sm:text-sm active:scale-95 active:shadow-sm transition-transform duration-100"
                onClick={() => {
                  setAppliedFilters((prev) => {
                    const next = { ...filterDraft };
                    return next;
                  });
                  setShowFilters(false);
                  setCurrentPage(1);
                }}
              >
                Apply Filter
              </button>
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader size={32} className="text-[#6B7143] animate-spin" />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 sm:px-6 py-4 rounded-lg text-xs sm:text-sm">
          {error}
        </div>
      ) : products.length > 0 ? (
        <>
          {/* --- TABLE --- */}
          <div className="border border-[#EEF1F3] rounded-lg overflow-x-auto">
            <table className="min-w-[700px] w-full text-left shadow-sm text-xs sm:text-sm">
              <thead className="bg-[#FBF8F6] text-[#7D828D] font-medium">
                <tr>
                  <th className="px-4 sm:px-6 py-3 sm:py-4">Product</th>
                  <th className="px-4 sm:px-6 py-3 sm:py-4">SKU</th>
                  <th className="px-4 sm:px-6 py-3 sm:py-4">Category</th>
                  <th className="px-4 sm:px-6 py-3 sm:py-4">Price</th>
                  <th className="px-4 sm:px-6 py-3 sm:py-4">Stock</th>
                  <th className="px-4 sm:px-6 py-3 sm:py-4">Status</th>
                  <th className="px-4 sm:px-6 py-3 sm:py-4">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#EEF1F3]">
                {products.map((product) => (
                  <tr
                    key={product.id}
                    className="hover:bg-slate-50/50 transition-colors"
                  >
                    <td className="px-4 sm:px-6 py-3 sm:py-4">
                      <div className="flex items-center gap-2 sm:gap-3">
                        {product.image_path && (
                          <img
                            src={product.image_path}
                            alt={product.title}
                            className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg object-cover bg-slate-100"
                          />
                        )}
                        <span className="font-medium text-slate-900">
                          {product.title}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4 font-medium text-slate-700">
                      {product.sku || "N/A"}
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4 text-slate-600">
                      {product.category?.name || "N/A"}
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4 font-medium text-slate-900">
                      {formatCurrency(product?.price || 0)}
                    </td>
                    <td
                      className={`px-4 sm:px-6 py-3 sm:py-4 font-bold ${
                        product.quantity === 0
                          ? "text-red-500"
                          : product.quantity < 10
                            ? "text-orange-500"
                            : "text-slate-700"
                      }`}
                    >
                      {product.quantity || 0}
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${
                          product.active_status === "active"
                            ? "bg-green-50 text-green-600"
                            : "bg-slate-100 text-slate-500"
                        }`}
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${product.active_status === "active" ? "bg-green-600" : "bg-slate-500"}`}
                        ></span>
                        {product.active_status === "active"
                          ? "Active"
                          : "Inactive"}
                      </span>
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4">
                      <div className="flex gap-2 sm:gap-4">
                        <button
                          onClick={() =>
                            navigate(`/product/edit/${product.id}`)
                          }
                          className="flex items-center gap-1 text-slate-700 hover:text-slate-900 font-medium text-xs sm:text-sm active:scale-95 transition-transform duration-100"
                        >
                          <Edit3 size={16} /> Edit
                        </button>
                        <button
                          onClick={() => handleDeleteClick(product)}
                          className="flex items-center gap-1 text-red-500 hover:text-red-600 font-medium text-xs sm:text-sm active:scale-95 transition-transform duration-100"
                        >
                          <Trash2 size={16} /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* --- PAGINATION --- */}
          {pagination && (
            <div className="p-4 sm:p-6 flex flex-col sm:flex-row gap-3 sm:gap-0 items-center justify-between border-t border-[#EEF1F3] bg-[#FBF8F6] w-full overflow-x-auto min-w-0">
              <span className="text-xs sm:text-sm text-[#7D828D]">
                Showing{" "}
                {pagination?.current_page
                  ? (pagination.current_page - 1) * pagination.per_page + 1
                  : 0}{" "}
                to{" "}
                {Math.min(
                  pagination?.current_page * pagination?.per_page,
                  pagination?.total,
                )}{" "}
                of {pagination?.total} products
              </span>
              <div className="w-full sm:w-auto">
                <Pagination
                  currentPage={currentPage}
                  totalPages={pagination?.last_page || 1}
                  onPageChange={setCurrentPage}
                />
              </div>
            </div>
          )}
        </>
      ) : (
        /* Empty State */
        <div className="flex flex-col items-center justify-center py-12 sm:py-16 px-4 sm:px-6 border border-[#EEF1F3] rounded-lg">
          <div className="p-1 border border-slate-300 bg-[#F8F5F1] rounded-full overflow-hidden mb-4">
            <Package size={32} className="text-[#BFAC93]" />
          </div>
          <h4 className="font-medium text-sm sm:text-base text-[#0C0C0E] text-center mb-2">
            No products yet
          </h4>
          <p className="text-xs sm:text-sm text-slate-400 text-center">
            Products will appear here once added
          </p>
        </div>
      )}

      {selectedProduct && (
        <DeleteModal
          isOpen={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          onDelete={() => {
            setDeleteModalOpen(false);
          }}
          onDeleteSuccess={() => setCurrentPage(1)}
          product={selectedProduct}
        />
      )}
    </div>
  );
};

export default Product;
